import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, XCircle, CheckCircle2, ClipboardList, ExternalLink } from 'lucide-react';
import { API } from '@/utils/constants';
import { CATALOG, SECTION_META, REQUIRED_BOLT_ITEM } from '@/pages/website/WeeklyProgramPage';

/**
 * Public, read-only view of a weekly program. Opened via the share link in
 * the email the admin sends to the patient — no login required, token valid
 * 30 days (`share_expires_at` on the server). Rendering stays intentionally
 * simple: a flat list of what's been prescribed per section, no interactive
 * state. Full write/check-off is still handled from the authenticated
 * `/mon-espace` view.
 */
const PublicWeeklyProgramPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: resp } = await axios.get(`${API}/public/weekly-program/${token}`);
        setData(resp);
      } catch (err) {
        setError(err.response?.data?.detail || 'Lien invalide ou expiré.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white px-6">
        <div className="max-w-sm text-center bg-white rounded-2xl p-8 shadow-md border border-rose-100">
          <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Lien non valide</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-500 mt-4">Demandez un nouveau lien à votre thérapeute.</p>
        </div>
      </div>
    );
  }

  const { program = {}, first_name = '' } = data || {};

  // Build the list of checked items for a given section, including any custom
  // free-text entries the therapist typed in and the required BOLT auto-item.
  const itemsForSection = (sectionKey, field) => {
    const stored = Array.isArray(program[field]) ? program[field] : [];
    const checked = stored.filter((it) => it && it.checked);
    const catalog = CATALOG[sectionKey] || [];
    return checked.map((it) => {
      if (it.custom) {
        return { id: it.id, label: it.label || '— Note du thérapeute —', description: it.note, custom: true };
      }
      const meta = catalog.find((c) => c.id === it.id) ||
        (it.id === REQUIRED_BOLT_ITEM.id ? REQUIRED_BOLT_ITEM : null);
      if (!meta) return null;
      return { ...meta, patientNote: it.patient_note };
    }).filter(Boolean);
  };

  const sections = [
    ['mecanique',  'mecanique_items'],
    ['biochimie',  'biochimie_items'],
    ['nerf_vague', 'nerf_vague_items'],
    ['mobilite',   'mobilite_items'],
    ['conseils',   'conseils_items'],
  ];
  const totalChecked = sections.reduce((n, [, field]) => n + ((program[field] || []).filter((i) => i.checked).length), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 py-10 px-4" data-testid="public-weekly-program-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo-mains-nuages.png" alt="Mains-Nuages" className="h-14 mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-light text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Programme respiratoire {first_name ? `de ${first_name}` : ''}
          </h1>
          <p className="text-sm text-slate-500">Préparé par votre physiothérapeute — <strong>{totalChecked}</strong> exercice{totalChecked > 1 ? 's' : ''} prescrit{totalChecked > 1 ? 's' : ''}</p>
        </div>

        {sections.map(([sectionKey, field]) => {
          const meta = SECTION_META[sectionKey];
          const items = itemsForSection(sectionKey, field);
          if (items.length === 0) return null;
          const Icon = meta.icon;
          return (
            <section key={sectionKey} className="mb-6 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <header className={`flex items-center gap-3 px-5 py-4 ${meta.bg} ${meta.border} border-b`}>
                <Icon size={22} className={meta.color} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{meta.title}</h2>
                </div>
              </header>
              <ul className="divide-y divide-slate-100">
                {items.map((it) => (
                  <li key={it.id} className="px-5 py-3 flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-sky-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-slate-800">{it.label}</span>
                        {it.href && (
                          <a href={it.href} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                            Voir <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                      {it.description && <p className="text-sm text-slate-600 mt-0.5">{it.description}</p>}
                      {it.patientNote && <p className="text-xs text-amber-700 mt-1 italic">Votre note : {it.patientNote}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {program.therapist_notes && (
          <section className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList size={18} className="text-amber-600" />
              <h3 className="font-semibold text-amber-900">Note du thérapeute</h3>
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap">{program.therapist_notes}</p>
          </section>
        )}

        <p className="text-center text-xs text-slate-400 mt-10">
          Lien privé — Physiothérapie Les Mains Nuages
        </p>
      </div>
    </div>
  );
};

export default PublicWeeklyProgramPage;
